import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { ErrorBanner } from "@/components/ErrorBanner";
import { useAuth } from "@/lib/AuthContext";
import { adminTagsApi } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { TagKind as TAG_KINDS, type TagKind, type TagRead, type TagTargetType } from "@/lib/types";

/** Reusable "Метки" block for an entity (user/station/device/order/flow).
 *
 * Lists active tag bindings and (with `tag.manage`) lets the admin bind a
 * new NFC/QR/Barcode/RFID or revoke an existing one. The backend
 * (`/admin/tags`) is the single source of truth.
 */
export function TagsSection({
  targetType,
  targetId,
  title,
  hint,
}: {
  targetType: TagTargetType;
  targetId: string;
  title?: string;
  hint?: string;
}) {
  const qc = useQueryClient();
  const { t } = useTranslation();
  const { user } = useAuth();
  const canManage = !!user?.permissions?.includes("tag.manage");

  const tagsQ = useQuery({
    queryKey: ["admin", "tags", "for", targetType, targetId],
    queryFn: async () => {
      switch (targetType) {
        case "operator":
          return adminTagsApi.forUser(targetId);
        case "station":
          return adminTagsApi.forStation(targetId);
        case "device":
          return adminTagsApi.forDevice(targetId);
        case "order":
          return adminTagsApi.forOrder(targetId);
        case "flow_item":
          return adminTagsApi.forFlow(targetId);
      }
    },
  });

  const [kind, setKind] = useState<TagKind>("NFC");
  const [value, setValue] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const bindM = useMutation({
    mutationFn: () =>
      adminTagsApi.create({
        kind,
        value: value.trim(),
        target_type: targetType,
        target_id: targetId,
      }),
    onSuccess: () => {
      setValue("");
      setErr(null);
      qc.invalidateQueries({ queryKey: ["admin", "tags", "for", targetType, targetId] });
      qc.invalidateQueries({ queryKey: ["admin", "tags", "all"] });
    },
    onError: (e) => setErr(e instanceof Error ? e.message : t("common.error")),
  });

  const unbindM = useMutation({
    mutationFn: (tagId: string) => adminTagsApi.unbind(tagId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "tags", "for", targetType, targetId] });
      qc.invalidateQueries({ queryKey: ["admin", "tags", "all"] });
    },
  });

  return (
    <Section title={title ?? t("tags.title")}>
      {hint && <p className="text-xs text-text-muted">{hint}</p>}

      {tagsQ.isLoading ? (
        <p className="text-text-muted text-sm">{t("common.loading")}</p>
      ) : tagsQ.isError ? (
        <p className="text-danger text-sm">
          {tagsQ.error instanceof Error ? tagsQ.error.message : t("common.error")}
        </p>
      ) : tagsQ.data!.length === 0 ? (
        <p className="text-text-muted text-sm italic">{t("tags.empty")}</p>
      ) : (
        <ul className="divide-y divide-border border border-border rounded-md overflow-hidden">
          {tagsQ.data!.map((tag) => (
            <TagRow
              key={tag.id}
              tag={tag}
              canManage={canManage}
              onUnbind={() => {
                if (confirm(t("tags.confirmUnbind", { kind: tag.kind, value: tag.value })))
                  unbindM.mutate(tag.id);
              }}
              busy={unbindM.isPending}
            />
          ))}
        </ul>
      )}

      {canManage && (
        <div className="space-y-2 pt-2 border-t border-border">
          {/* Banner sits ABOVE the form so the user can't miss it after
              hitting Привязать and getting a 409 ("метка уже занята"). */}
          {err && <ErrorBanner onDismiss={() => setErr(null)}>{err}</ErrorBanner>}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setErr(null);
              if (!value.trim()) {
                setErr(t("tags.enterValue"));
                return;
              }
              bindM.mutate();
            }}
            className="flex flex-wrap gap-2 items-end"
          >
            <label className="block">
              <span className="text-xs font-mono uppercase tracking-wider text-text-muted">{t("tags.type")}</span>
              <select
                className="mt-1 px-3 py-2 rounded-md bg-surface border border-border font-mono text-xs"
                value={kind}
                onChange={(e) => setKind(e.target.value as TagKind)}
              >
                {TAG_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </select>
            </label>
            <label className="block flex-1 min-w-[12rem]">
              <span className="text-xs font-mono uppercase tracking-wider text-text-muted">
                {t("tags.value")}
              </span>
              <input
                className="mt-1 w-full px-3 py-2 rounded-md bg-surface border border-border font-mono"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={placeholderFor(kind)}
              />
            </label>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={bindM.isPending || !value.trim()}
            >
              {bindM.isPending ? t("tags.binding") : t("tags.bind")}
            </button>
          </form>
        </div>
      )}
    </Section>
  );
}

function TagRow({
  tag,
  canManage,
  onUnbind,
  busy,
}: {
  tag: TagRead;
  canManage: boolean;
  onUnbind: () => void;
  busy: boolean;
}) {
  const { t } = useTranslation();
  return (
    <li className="flex items-center gap-3 px-3 py-2 text-sm">
      <span className="pill pill-accent font-mono text-xs">{tag.kind}</span>
      <span className="font-mono text-text-primary truncate" title={tag.value}>
        {tag.value}
      </span>
      <span className="ml-auto text-xs text-text-muted font-mono">
        {tag.last_seen_at
          ? t("tags.lastScan", { date: formatDateTime(tag.last_seen_at) })
          : t("tags.created", { date: formatDateTime(tag.created_at) })}
      </span>
      {canManage && (
        <button
          type="button"
          className="btn btn-danger text-xs"
          onClick={onUnbind}
          disabled={busy}
          title={t("tags.unbindTitle")}
        >
          {t("tags.unbind")}
        </button>
      )}
    </li>
  );
}

function placeholderFor(kind: TagKind): string {
  switch (kind) {
    case "NFC":
      return "NFC_OP_001";
    case "QR":
      return "QR_STN_3";
    case "BARCODE":
      return "0123456789012";
    case "RFID":
      return "RFID-0001";
  }
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="card p-5 space-y-4">
      <h2 className="text-lg">{title}</h2>
      {children}
    </section>
  );
}
